/**
 * Public embeddable signup page.
 *
 * URL: ``/embed/signup``. Designed to be iframed from the WordPress site
 * (or any other) with a single line of HTML:
 *
 *   <iframe src="https://<app-domain>/embed/signup"
 *           style="width:100%;height:1050px;border:0;"
 *           title="Signup"></iframe>
 *
 * This route is not protected — it hits the public ``/api/public/signup``
 * endpoint which does its own rate limiting + honeypot.
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, MapPin, Trash2, Loader2, AlertCircle } from "lucide-react";
import { company } from "@/lib/branding";

const CART_SIZES = [
    { value: "96gal", label: "96 gallon (standard)" },
    { value: "64gal", label: "64 gallon (small)" },
    { value: "35gal", label: "35 gallon (extra small)" },
];
const CART_QTYS = [1, 2, 3, 4];
const DAYS = [
    { value: "no_preference", label: "No preference" },
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
];

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmbedSignup() {
    const [form, setForm] = useState({
        name: "", email: "", phone: "",
        line1: "", line2: "", city: "", state: "TX", zip: "",
        cart_quantity: 1, cart_size: "96gal",
        preferred_day: "no_preference", notes: "",
        website: "", // honeypot — must stay empty
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    // Real-time service-area check as the customer fills their address.
    const [areaState, setAreaState] = useState({ status: "idle" });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Debounced address → service-area check.
    useEffect(() => {
        const line1 = form.line1?.trim();
        const city = form.city?.trim();
        const zip = form.zip?.trim();
        if (!line1 || !city || zip.length < 3) {
            setAreaState({ status: "idle" });
            return;
        }
        const addr = `${line1}, ${city}, ${form.state} ${zip}`;
        setAreaState({ status: "checking" });
        const t = setTimeout(async () => {
            try {
                const res = await fetch(
                    `${API}/api/public/service-area/check?address=${encodeURIComponent(addr)}`,
                );
                const data = await res.json();
                if (!data.configured) {
                    setAreaState({ status: "not_configured" });
                } else if (data.in_area) {
                    setAreaState({ status: "in_area", name: data.area_name });
                } else if (data.in_area === false) {
                    setAreaState({ status: "out_of_area" });
                } else {
                    setAreaState({ status: "unknown" });
                }
            } catch {
                setAreaState({ status: "error" });
            }
        }, 700);
        return () => clearTimeout(t);
    }, [form.line1, form.city, form.state, form.zip]);

    // Post a resize message so a lightweight WordPress iframe wrapper can
    // auto-shrink after the confirmation screen appears.
    useEffect(() => {
        const send = () => {
            const h = document.documentElement.scrollHeight;
            try { window.parent?.postMessage({ type: "roseDisposalSignup:height", height: h }, "*"); } catch { /* cross-origin */ }
        };
        send();
        const ro = new ResizeObserver(send);
        ro.observe(document.documentElement);
        return () => ro.disconnect();
    }, [done]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                preferred_day: form.preferred_day === "no_preference" ? null : form.preferred_day,
            };
            const res = await fetch(`${API}/api/public/signup`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || "Signup failed");
            }
            setDone(true);
        } catch (err) {
            setError(err.message || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
                <Card className="max-w-md w-full" data-testid="embed-signup-success">
                    <CardContent className="p-8 text-center">
                        <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-4" />
                        <h1 className="font-heading text-2xl font-bold">Thanks, {form.name.split(" ")[0]}!</h1>
                        <p className="text-sm text-muted-foreground mt-2">
                            We&apos;ve received your signup and will reach out within one business
                            day to confirm your first pickup and get your cart delivered.
                        </p>
                        <div className="mt-6 text-xs text-muted-foreground">
                            <div>Questions? Call the office anytime.</div>
                            <div className="mt-2 font-mono">Confirmation logged · {new Date().toLocaleDateString()}</div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background px-4 py-6" data-testid="embed-signup-page">
            <div className="max-w-lg mx-auto">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Trash2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <div className="overline text-[10px]">{company()}</div>
                        <h1 className="font-heading text-xl font-bold leading-tight">Start weekly service</h1>
                    </div>
                </div>

                <Card>
                    <CardContent className="p-5">
                        <form onSubmit={submit} className="space-y-4">
                            {/* Honeypot — hidden from humans */}
                            <input type="text" name="website" value={form.website}
                                   onChange={(e) => set("website", e.target.value)}
                                   tabIndex={-1} autoComplete="off"
                                   style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
                                   aria-hidden="true" />

                            <Field label="Your name">
                                <Input required value={form.name}
                                       onChange={(e) => set("name", e.target.value)}
                                       data-testid="signup-name" placeholder="Jane Smith" />
                            </Field>

                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Email">
                                    <Input required type="email" value={form.email}
                                           onChange={(e) => set("email", e.target.value)}
                                           data-testid="signup-email" placeholder="jane@example.com" />
                                </Field>
                                <Field label="Phone">
                                    <Input required type="tel" value={form.phone}
                                           onChange={(e) => set("phone", e.target.value)}
                                           data-testid="signup-phone" placeholder="(512) 555-1234" />
                                </Field>
                            </div>

                            <div className="pt-1 pb-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                                <MapPin className="w-3.5 h-3.5" />Service address
                            </div>
                            <Field label="Street">
                                <GoogleAddressInput
                                    value={form.line1}
                                    onChange={(v) => set("line1", v)}
                                    onAddressSelected={(a) => setForm((c) => ({
                                        ...c,
                                        line1: a.line1 || c.line1,
                                        city: a.city || c.city,
                                        state: a.state || c.state,
                                        zip: a.zip || c.zip,
                                    }))}
                                    placeholder="1011 County Road 217"
                                    testid="signup-line1"
                                />
                            </Field>
                            <Field label="Apt / Unit (optional)">
                                <Input value={form.line2}
                                       onChange={(e) => set("line2", e.target.value)}
                                       data-testid="signup-line2" />
                            </Field>
                            <div className="grid grid-cols-3 gap-3">
                                <Field label="City">
                                    <Input required value={form.city}
                                           onChange={(e) => set("city", e.target.value)}
                                           data-testid="signup-city" placeholder="Florence" />
                                </Field>
                                <Field label="State">
                                    <Input required value={form.state}
                                           onChange={(e) => set("state", e.target.value)}
                                           data-testid="signup-state" />
                                </Field>
                                <Field label="ZIP">
                                    <Input required value={form.zip}
                                           onChange={(e) => set("zip", e.target.value)}
                                           data-testid="signup-zip" placeholder="76527" />
                                </Field>
                            </div>

                            {areaState.status === "in_area" && (
                                <div className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded p-2"
                                     data-testid="signup-area-in">
                                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>Address is inside our service area{areaState.name ? ` (${areaState.name})` : ""}. You&apos;re good to sign up!</span>
                                </div>
                            )}
                            {areaState.status === "out_of_area" && (
                                <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2"
                                     data-testid="signup-area-out">
                                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>Your address looks like it&apos;s outside our current service area. You can still sign up &mdash; we&apos;ll add you to the waitlist and call if a route opens near you.</span>
                                </div>
                            )}
                            {areaState.status === "checking" && (
                                <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Checking service area…
                                </div>
                            )}

                            <div className="pt-2 grid grid-cols-2 gap-3">
                                <Field label="How many carts?">
                                    <Select value={String(form.cart_quantity)}
                                            onValueChange={(v) => set("cart_quantity", parseInt(v, 10))}>
                                        <SelectTrigger data-testid="signup-cart-qty"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {CART_QTYS.map((n) => (
                                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Cart size">
                                    <Select value={form.cart_size}
                                            onValueChange={(v) => set("cart_size", v)}>
                                        <SelectTrigger data-testid="signup-cart-size"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {CART_SIZES.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>

                            <Field label="Preferred pickup day">
                                <Select value={form.preferred_day}
                                        onValueChange={(v) => set("preferred_day", v)}>
                                    <SelectTrigger data-testid="signup-day"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {DAYS.map((d) => (
                                            <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>

                            <Field label="Anything the office should know? (optional)">
                                <textarea value={form.notes}
                                          onChange={(e) => set("notes", e.target.value)}
                                          rows={3}
                                          data-testid="signup-notes"
                                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                                          placeholder="Gate code, dog on property, cart placement, etc." />
                            </Field>

                            {error && (
                                <div className="text-xs text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/20 rounded p-2"
                                     data-testid="signup-error">
                                    {error}
                                </div>
                            )}

                            <Button type="submit" className="w-full h-11 text-base"
                                    disabled={submitting}
                                    data-testid="signup-submit">
                                {submitting ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</>
                                ) : "Sign up for service"}
                            </Button>

                        <p className="text-[10px] text-muted-foreground text-center">
                            By signing up you authorize {company()} to contact you about your
                            service. No payment collected today &mdash; the office confirms first.
                        </p>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium">{label}</Label>
            {children}
        </div>
    );
}
